import { useCallback, useEffect, useMemo, useState } from 'react';
import { storageApiRef, useApi } from '@backstage/core-plugin-api';
import {
  DEFAULT_QUICK_LINKS,
  MAX_QUICK_LINKS,
  QuickLink,
  ValidationError,
} from './types';

const BUCKET = 'quickLinks';
const KEY = 'links';

export type SaveResult = { ok: true } | { ok: false; error: ValidationError };

function validateLinks(links: QuickLink[]): ValidationError | null {
  if (links.length > MAX_QUICK_LINKS) {
    return { type: 'too_many', max: MAX_QUICK_LINKS, actual: links.length };
  }
  for (let i = 0; i < links.length; i++) {
    if (!links[i].title.trim()) {
      return { type: 'empty_title', index: i };
    }
    try {
      const { protocol } = new URL(links[i].url);
      if (protocol !== 'http:' && protocol !== 'https:') {
        return {
          type: 'invalid_url',
          title: links[i].title,
          url: links[i].url,
        };
      }
    } catch {
      return { type: 'invalid_url', title: links[i].title, url: links[i].url };
    }
  }
  return null;
}

export function useQuickLinks(): {
  links: QuickLink[];
  loading: boolean;
  saveLinks: (links: QuickLink[]) => Promise<SaveResult>;
} {
  const storageApi = useApi(storageApiRef);
  const bucket = useMemo(() => storageApi.forBucket(BUCKET), [storageApi]);

  const [links, setLinks] = useState<QuickLink[]>(DEFAULT_QUICK_LINKS);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const subscription = (bucket as any)
      .observe$(KEY)
      .subscribe((snapshot: { presence: string; value?: QuickLink[] }) => {
        if (snapshot.presence === 'present') {
          setLinks(snapshot.value as QuickLink[]);
        } else if (snapshot.presence === 'absent') {
          setLinks(DEFAULT_QUICK_LINKS);
        }
        // Keep loading true only while presence is 'unknown'
        setLoading(snapshot.presence === 'unknown');
      });
    return () => subscription.unsubscribe();
  }, [bucket]);

  const saveLinks = useCallback(
    async (newLinks: QuickLink[]): Promise<SaveResult> => {
      const error = validateLinks(newLinks);
      if (error) {
        return { ok: false, error };
      }
      await bucket.set(KEY, newLinks as any);
      return { ok: true };
    },
    [bucket],
  );

  return { links, loading, saveLinks };
}
